// Ambassador payout approval log: status cell + per-ambassador dated history.
import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

const GOLD = "#B8913A";
const btn = { padding: "4px 10px", borderRadius: 999, border: `1px solid ${GOLD}`, background: "transparent", color: GOLD, cursor: "pointer", fontSize: 11 };
const input = { padding: "4px 6px", border: "1px solid #D9D2C2", borderRadius: 6, fontSize: 11, width: 130, fontFamily: "inherit" };
const STATUS_COLOR = { pending: "#7A7466", approved: "#B8913A", paid: "#2E7D4F" };
const ACTION_LABEL = { approved: "Approved", paid: "Marked paid", approval_reverted: "Approval undone", payment_reverted: "Payment undone" };
const dt = (s) => (s ? new Date(s).toLocaleString() : "—");
const money = (n) => (n == null ? "—" : `$${Number(n).toFixed(2)}`);

function InlineAction({ placeholder, label, testid, onSubmit, onCancel }) {
  const [v, setV] = useState("");
  return (
    <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
      <input style={input} placeholder={placeholder} value={v} onChange={(e) => setV(e.target.value)} data-testid={`${testid}-input`} autoFocus />
      <button style={btn} onClick={() => onSubmit(v)} data-testid={`${testid}-confirm`}>{label}</button>
      <button style={{ ...btn, borderColor: "#ccc", color: "#7A7466" }} onClick={onCancel} data-testid={`${testid}-cancel`}>×</button>
    </div>
  );
}

export function PayoutCell({ a, month, onChanged }) {
  const [mode, setMode] = useState(null);
  const po = a.payout || { status: "pending" };
  const base = `/admin/ambassadors/${a.ambassador_id}/payouts/${month}`;
  const call = async (path, body, ok) => {
    try { await api.post(`${base}/${path}`, body); toast.success(ok); setMode(null); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Payout update failed"); }
  };
  const canApprove = po.status === "pending" && a.payout_month_closed && a.month_compensation > 0;
  return (
    <div data-testid={`ambassador-payout-${a.code}`}>
      <span style={{ color: STATUS_COLOR[po.status], fontWeight: 600, textTransform: "capitalize" }} data-testid={`ambassador-payout-status-${a.code}`}>{po.status}</span>
      {po.status !== "pending" && <div style={{ fontSize: 11, color: "#7A7466" }}>{money(po.snapshot?.total_pay)} approved {dt(po.approved_at)}</div>}
      {po.status === "paid" && <div style={{ fontSize: 11, color: "#7A7466" }}>Paid {dt(po.paid_at)}{po.reference ? ` · ${po.reference}` : ""}</div>}
      {po.status === "pending" && !a.payout_month_closed && <div style={{ fontSize: 11, color: "#7A7466" }}>Approve after month ends</div>}
      {!mode && (
        <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
          {canApprove && <button style={btn} onClick={() => call("approve", {}, "Payout approved")} data-testid={`ambassador-payout-approve-${a.code}`}>Approve</button>}
          {po.status === "approved" && <button style={btn} onClick={() => setMode("paid")} data-testid={`ambassador-payout-paid-${a.code}`}>Mark paid</button>}
          {po.status !== "pending" && <button style={btn} onClick={() => setMode("undo")} data-testid={`ambassador-payout-undo-${a.code}`}>Undo</button>}
        </div>
      )}
      {mode === "paid" && <InlineAction placeholder="Reference (optional)" label="Confirm" testid={`ambassador-payout-paid-${a.code}`}
        onSubmit={(v) => call("mark-paid", { reference: v || null }, "Marked paid")} onCancel={() => setMode(null)} />}
      {mode === "undo" && <InlineAction placeholder="Reason (required)" label="Undo" testid={`ambassador-payout-undo-${a.code}`}
        onSubmit={(v) => (v.trim().length < 3 ? toast.error("Add a reason (3+ characters)") : call("revert", { reason: v }, "Undone"))} onCancel={() => setMode(null)} />}
    </div>
  );
}

export function PayoutHistory({ id, version }) {
  const [events, setEvents] = useState(null);
  useEffect(() => {
    api.get(`/admin/ambassadors/${id}/payouts`).then((r) => setEvents(r.data.events)).catch(() => setEvents([]));
  }, [id, version]);
  if (!events) return null;
  return (
    <div style={{ marginTop: 12 }} data-testid={`ambassador-payout-history-${id}`}>
      <div style={{ fontSize: 11, letterSpacing: 1, textTransform: "uppercase", color: "#7A7466", marginBottom: 4 }}>Payout history</div>
      {!events.length && <div style={{ fontSize: 12 }}>No payout activity yet.</div>}
      {events.map((e) => (
        <div key={e.event_id} style={{ fontSize: 12, padding: "3px 0" }} data-testid={`ambassador-payout-event-${e.event_id}`}>
          <strong>{e.month}</strong> · {ACTION_LABEL[e.action] || e.action} · {dt(e.at)} · {e.by}
          {e.snapshot && ` · ${money(e.snapshot.total_pay)} (${e.snapshot.registrations} regs)`}
          {e.reference && ` · ref ${e.reference}`}
          {e.reason && ` · reason: ${e.reason}`}
        </div>
      ))}
    </div>
  );
}
